import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import {
    Box, Grid, Typography, TextField, InputAdornment, CircularProgress, Button,
    Pagination, IconButton, Tooltip, Breadcrumbs, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
    Checkbox, FormControlLabel, Alert,
} from '@mui/material';
import {
    Search, PushPin, PushPinOutlined, ArrowBack, UploadFile, Add,
    AccessTime, Download, Edit, DriveFileMove, LinkOutlined,
    PublicOutlined, Star, StarBorder,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';

import { ThemeContext } from '../ThemeContext/ThemeContext';
import ConteudoFormatado from '../ConteudoFormatado';
import ArvoreDeSecoes from './ArvoreDeSecoes';
import {
    listarSecoes, listarArtigos, lerArtigo, criarSecao, renomearSecao, apagarSecao,
    createDocument, updateDocument, uploadDocument, downloadDocument,
    favoritarMaterial, desfavoritarMaterial,
} from '../../services/aiDocumentsService';

/**
 * A CENTRAL DE AJUDA.
 *
 * ## O pedido
 *
 * Dono do produto, 20/10/2026, depois de validarmos a central que a SysFar
 * mantém hoje no Movidesk (`sysfar.movidesk.com/kb/pt-br/`): "colocar como se
 * fosse aquela página, mas com nosso design, para ser uma base de conhecimento
 * que temos ali para alimentar e também ela alimentar o Orby".
 *
 * ## O que foi copiado de lá, e o que não
 *
 * Copiado porque é o que faz a navegação funcionar: árvore de seções à
 * esquerda, lista com título + resumo + paginação, e o artigo com a trilha
 * (`Fiscal > NF-e > NF-e Entrada`), tempo de leitura e autoria.
 *
 * Não copiado: o visual deles. O pedido foi explícito -- "com nosso design".
 *
 * ## Alimentar e ser alimentado
 *
 * Escrever artigo e enviar arquivo passam por `/ai-documents`, que é quem
 * indexa no Qdrant. É isso que mantém a segunda metade do pedido: o que entra
 * aqui o Orby responde.
 *
 * ## Interna agora, do cliente depois
 *
 * Cada artigo tem "visível para o cliente", gravado desde já e nascendo FALSO.
 * A página pública é a segunda etapa; o campo existe para essa etapa não exigir
 * remexer em cada artigo depois.
 */

const POR_PAGINA = 10;

/** O artigo em branco -- é o estado de "escrever novo". */
const ARTIGO_EM_BRANCO = {
    id: null,
    title: '',
    content: '',
    kb_category_id: '',
    visivel_para_cliente: false,
    origem_url: null,
};

/**
 * O editor rico considera vazio como `<p><br></p>`.
 *
 * Sem isto, publicar um artigo sem escrever nada passaria pela validação e
 * criaria na base um documento com uma tag dentro -- que o Orby indexaria.
 */
const textoDe = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const dataLegivel = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString();
    } catch (e) {
        return '';
    }
};

/*
 * `podeFixar` e SEPARADO de `podeAdministrar` (Gisele, 23/10/2026).
 *
 * Administrar a central e mexer na ARVORE: criar, renomear, mover e apagar
 * secao reorganiza tudo para todo mundo. Fixar e outra coisa -- decide o que
 * a equipe ve primeiro --, e o pedido dela e justamente poder dar uma sem a
 * outra ("definir quem podera fixar: operador ou somente administrador").
 *
 * O padrao e `false`: quem nao passar a prop nao ganha o alfinete de graca.
 */
const CentralDeAjuda = ({ podeAdministrar = false, podeFixar = false }) => {
    const { theme } = useContext(ThemeContext) || {};

    const [secoes, setSecoes] = useState([]);
    const [semCategoria, setSemCategoria] = useState(0);
    const [selecionada, setSelecionada] = useState('todas');

    const [artigos, setArtigos] = useState([]);
    const [pagina, setPagina] = useState(1);
    const [totalDePaginas, setTotalDePaginas] = useState(1);
    const [total, setTotal] = useState(0);
    const [busca, setBusca] = useState('');
    const [buscaAplicada, setBuscaAplicada] = useState('');
    /*
     * "Somente fixados" (Gisele, 23/10/2026).
     *
     * Vai para o SERVIDOR, junto da busca e da secao: a lista e paginada,
     * e recortar no navegador devolveria "os fixados desta pagina" -- numa
     * base de centenas de artigos, a pagina 1 mostraria dois fixados
     * havendo dez. E o mesmo defeito da busca avancada de chamados.
     */
    const [somenteFixados, setSomenteFixados] = useState(false);
    /*
     * "Meus favoritos" é um recorte SEPARADO de "Somente fixados", e os dois
     * compõem: são perguntas diferentes ("o que a equipe destacou" e "o que
     * eu guardei"). E o recorte é do SERVIDOR, como o dos fixados -- esta
     * lista vem paginada, e filtrar no navegador devolveria "os meus
     * favoritos que por acaso estão nesta página".
     */
    const [somenteFavoritos, setSomenteFavoritos] = useState(false);

    const [carregando, setCarregando] = useState(true);
    const [falhou, setFalhou] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const [artigoAberto, setArtigoAberto] = useState(null);
    const [carregandoArtigo, setCarregandoArtigo] = useState(false);

    const [dialogArtigo, setDialogArtigo] = useState(false);
    const [formArtigo, setFormArtigo] = useState(ARTIGO_EM_BRANCO);
    const [dialogSecao, setDialogSecao] = useState(null);
    const [nomeDaSecao, setNomeDaSecao] = useState('');

    // Os artigos marcados para mover de uma vez. Por id, e não pelo objeto: a
    // lista é recarregada depois de cada operação e os objetos são outros.
    const [marcados, setMarcados] = useState([]);
    const [dialogMover, setDialogMover] = useState(false);
    const [destinoDaMudanca, setDestinoDaMudanca] = useState('');

    const carregarSecoes = useCallback(async () => {
        try {
            const data = await listarSecoes();
            setSecoes(Array.isArray(data?.data) ? data.data : []);
            setSemCategoria(Number(data?.sem_categoria) || 0);
        } catch (e) {
            // A árvore falhar não pode derrubar a lista: dá para ler os artigos
            // sem o menu, e um menu vazio é menos ruim que a tela em branco.
            setSecoes([]);
        }
    }, []);

    const carregarArtigos = useCallback(async () => {
        setCarregando(true);
        try {
            const params = { page: pagina, per_page: POR_PAGINA };
            if (selecionada !== 'todas' && selecionada !== 'sem-categoria') {
                params.kb_category_id = selecionada;
            }
            if (selecionada === 'sem-categoria') params.sem_categoria = 1;
            if (buscaAplicada.trim() !== '') params.q = buscaAplicada.trim();
            if (somenteFixados) params.fixados = 1;
            if (somenteFavoritos) params.favoritos = 1;

            const data = await listarArtigos(params);
            setArtigos(Array.isArray(data?.data) ? data.data : []);
            setTotalDePaginas(Number(data?.last_page) || 1);
            setTotal(Number(data?.total) || 0);
            setFalhou(false);
        } catch (e) {
            // Lista vazia e lista que não carregou são estados DIFERENTES:
            // "nenhum artigo" depois de um erro de rede faz a pessoa cadastrar
            // de novo o que já existe.
            setFalhou(true);
            setArtigos([]);
        } finally {
            setCarregando(false);
        }
    }, [pagina, selecionada, buscaAplicada, somenteFixados, somenteFavoritos]);

    useEffect(() => { carregarSecoes(); }, [carregarSecoes]);
    useEffect(() => { carregarArtigos(); }, [carregarArtigos]);

    const escolherSecao = (id) => {
        setSelecionada(id);
        // Volta para a primeira página: a página 4 da seção anterior quase
        // nunca existe na nova, e o resultado seria uma lista vazia que parece
        // seção sem conteúdo.
        setPagina(1);
        setArtigoAberto(null);
        /*
         * E limpa o endereço junto. Trocar de seção FECHA o artigo, e deixar
         * o `?artigo=` para trás faria a central reabri-lo na próxima vez que
         * a tela montasse -- um artigo que a pessoa já tinha deixado, voltando
         * sozinho.
         */
        escreverEndereco(null);
    };

    /** O endereco da pagina, com (ou sem) o artigo aberto. */
    const escreverEndereco = useCallback((id) => {
        const endereco = new URL(window.location.href);

        if (id) {
            endereco.searchParams.set('artigo', String(id));
        } else {
            endereco.searchParams.delete('artigo');
        }

        /*
         * `replaceState` e nao `pushState`: abrir e fechar artigos encheria
         * o historico, e o botao Voltar passaria a desfazer leituras em vez
         * de sair da central.
         */
        window.history.replaceState({}, '', endereco.toString());
    }, []);

    /**
     * Abre o artigo e DEIXA O ENDERECO apontando para ele.
     *
     * E esse endereco que vira o "link individual" do pedido: sem ele a
     * central e uma tela so, e nao ha o que copiar nem para onde o Orby
     * apontar.
     */
    const abrirArtigo = useCallback(async (id) => {
        setCarregandoArtigo(true);
        try {
            const data = await lerArtigo(id);
            setArtigoAberto(data?.data || null);

            if (data?.data?.id) escreverEndereco(data.data.id);
        } catch (e) {
            toast.error('Não foi possível abrir o artigo.', { position: toast.POSITION.BOTTOM_CENTER });
        } finally {
            setCarregandoArtigo(false);
        }
    }, [escreverEndereco]);

    /**
     * Copia o endereco deste artigo.
     *
     * O endereco e montado a partir da pagina ATUAL (`window.location`) e
     * nao de uma constante: a central vive dentro da pagina de videos, e um
     * caminho escrito a mao aqui ficaria errado no dia em que a aba mudar de
     * lugar -- e ficaria errado em silencio, num link ja compartilhado.
     *
     * `navigator.clipboard` nao existe fora de https nem em navegador
     * antigo. Sem o plano B o botao ficaria mudo justamente para quem mais
     * precisa de ajuda.
     */
    /**
     * Copia um endereco para a area de transferencia.
     *
     * `navigator.clipboard` nao existe fora de https nem em navegador
     * antigo. Sem o plano B o botao ficaria mudo justamente para quem mais
     * precisa de ajuda.
     */
    const copiar = async (link, aviso) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
            } else {
                const caixa = document.createElement('textarea');
                caixa.value = link;
                document.body.appendChild(caixa);
                caixa.select();
                document.execCommand('copy');
                document.body.removeChild(caixa);
            }
            toast.success(aviso, { position: toast.POSITION.BOTTOM_CENTER });
        } catch (e) {
            toast.error('Não foi possível copiar o link.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    /**
     * O link PUBLICO, para mandar ao cliente.
     *
     * Quem monta o endereco e o servidor (`link_publico`): a tela sabe o id,
     * mas nao o slug da empresa nem a base da api -- e o formato da URL
     * passaria a existir em dois lugares, que divergem no primeiro ajuste
     * feito num so. Um link ja mandado a um cliente nao da para corrigir.
     */
    const copiarParaOCliente = (artigo) => copiar(
        artigo.link_publico,
        'Link copiado. Esta página abre sem login -- pode mandar ao cliente.',
    );

    /**
     * O link INTERNO, para mandar a um colega.
     *
     * Montado a partir da página ATUAL e não de uma constante: a central vive
     * dentro da página de vídeos, e um caminho escrito à mão aqui ficaria
     * errado no dia em que a aba mudar de lugar -- e ficaria errado em
     * silêncio, num link já compartilhado.
     */
    const copiarLinkDoArtigo = (artigo) => {
        const endereco = new URL(window.location.href);
        endereco.searchParams.set('artigo', String(artigo.id));

        return copiar(endereco.toString(), 'Link do artigo copiado.');
    };

    const fecharArtigo = () => {
        setArtigoAberto(null);
        escreverEndereco(null);
    };

    /*
     * Chegou por um link? Abre o artigo direto.
     *
     * Roda UMA vez: sem a trava, fechar o artigo limparia o endereco, o
     * efeito veria a mudanca e abriria de novo -- a tela nao deixaria
     * ninguem sair do artigo.
     */
    const jaAbriuPeloEndereco = useRef(false);
    useEffect(() => {
        if (jaAbriuPeloEndereco.current) return;

        const id = new URLSearchParams(window.location.search).get('artigo');

        if (!id) return;

        jaAbriuPeloEndereco.current = true;
        abrirArtigo(id);
    }, [abrirArtigo]);

    const alternarFixado = async (artigo) => {
        const novo = !artigo.fixado;
        try {
            await updateDocument(artigo.id, { fixado: novo });
            setArtigos((atuais) => atuais.map((a) => (a.id === artigo.id ? { ...a, fixado: novo } : a)));
            toast.success(novo ? 'Fixado no topo.' : 'Desfixado.', { position: toast.POSITION.BOTTOM_CENTER });
        } catch (e) {
            toast.error('Não foi possível fixar o artigo.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    /**
     * Favoritar e desfavoritar o artigo — o destaque DELE, não o da equipe.
     *
     * ## Por que está aqui também, e não só nos vídeos
     *
     * Porque a aba Documentos é esta central, com lista própria: deixar a
     * estrela só na grade de vídeos entregaria metade do pedido — o operador
     * guardaria um treinamento e não guardaria um manual, sem nada na tela
     * explicando a diferença.
     *
     * O alfinete ao lado é o destaque institucional e tem permissão
     * (`podeFixar`). A estrela não tem, porque só mexe na ordem da tela de
     * quem clicou.
     */
    const alternarFavorito = async (artigo) => {
        const novo = !artigo.favorito;
        try {
            if (novo) {
                await favoritarMaterial(artigo.id);
            } else {
                await desfavoritarMaterial(artigo.id);
            }
            setArtigos((atuais) => atuais.map((a) => (a.id === artigo.id ? { ...a, favorito: novo } : a)));
        } catch (e) {
            // Devolve o estado: uma estrela acesa sem ter salvado faz a
            // pessoa sair achando que guardou.
            toast.error('Não foi possível salvar o favorito.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    const enviarArquivo = async (arquivo) => {
        if (!arquivo) return;
        const dados = new FormData();
        dados.append('file', arquivo);

        setSalvando(true);
        try {
            await uploadDocument(dados);
            toast.success('Documento enviado.', { position: toast.POSITION.BOTTOM_CENTER });
            carregarArtigos();
            carregarSecoes();
        } catch (e) {
            toast.error(
                e?.response?.data?.error || 'Não foi possível enviar o documento.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Abre o artigo para EDIÇÃO, com o conteúdo completo.
     *
     * O conteúdo completo precisa vir do servidor: a lista traz `resumo`, que é
     * o texto cortado para caber no cartão. Abrir o editor com o resumo e
     * salvar apagaria o artigo inteiro, deixando no lugar as suas primeiras
     * linhas -- e o Orby reindexaria essa versão truncada.
     */
    const abrirEdicao = async (artigo) => {
        setCarregandoArtigo(true);
        try {
            const data = await lerArtigo(artigo.id);
            const completo = data?.data || {};

            setFormArtigo({
                id: artigo.id,
                title: completo.title ?? artigo.title ?? '',
                content: completo.content ?? '',
                kb_category_id: completo.kb_category_id ?? '',
                visivel_para_cliente: Boolean(completo.visivel_para_cliente),
                origem_url: completo.origem_url ?? null,
            });
            setDialogArtigo(true);
        } catch (e) {
            toast.error('Não foi possível abrir o artigo para edição.', { position: toast.POSITION.BOTTOM_CENTER });
        } finally {
            setCarregandoArtigo(false);
        }
    };

    const salvarArtigo = async () => {
        const titulo = formArtigo.title.trim();
        const editando = formArtigo.id !== null;

        if (titulo === '' || textoDe(formArtigo.content) === '') {
            toast.error('Informe o título e o conteúdo do artigo.', { position: toast.POSITION.BOTTOM_CENTER });
            return;
        }

        const campos = {
            title: titulo,
            content: formArtigo.content,
            kb_category_id: formArtigo.kb_category_id || null,
            visivel_para_cliente: formArtigo.visivel_para_cliente,
        };

        setSalvando(true);
        try {
            // Pelo endpoint de DOCUMENTO nos dois casos: é ele que indexa no
            // Orby, e é ele que reindexa quando o texto muda.
            if (editando) {
                await updateDocument(formArtigo.id, campos);
            } else {
                await createDocument(campos);
            }
            toast.success(
                editando ? 'Artigo atualizado.' : 'Artigo publicado na central.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
            setDialogArtigo(false);
            setFormArtigo(ARTIGO_EM_BRANCO);
            carregarArtigos();
            carregarSecoes();
            // O artigo aberto na tela ficaria mostrando a versão antiga.
            if (artigoAberto?.id === formArtigo.id) abrirArtigo(formArtigo.id);
        } catch (e) {
            toast.error(
                e?.response?.data?.error
                    || (editando ? 'Não foi possível salvar o artigo.' : 'Não foi possível publicar o artigo.'),
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Move de uma vez os artigos marcados para uma seção.
     *
     * ## Por que em lote, e não só pelo diálogo de edição
     *
     * São 402 artigos sem seção em produção (medido em 21/10), herdados da
     * importação do Movidesk e da base que existia antes da central. Um por um,
     * cada um exigiria abrir o editor, esperar o conteúdo completo carregar,
     * escolher a seção e salvar -- 402 vezes. A organização simplesmente não
     * seria feita.
     *
     * ## Uma requisição por artigo, e por que isso está bem
     *
     * Não há endpoint de lote na api. Mover 10 artigos são 10 PUTs, e cada um
     * sai barato porque mexer só em `kb_category_id` NÃO reindexa no Qdrant --
     * o `update` compara os campos que importam e responde
     * `no_relevant_changes`. Organizar a base não custa embedding nenhum.
     *
     * As falhas são contadas e informadas em vez de abortar no primeiro erro:
     * com 10 artigos, parar no meio deixaria metade movida sem dizer qual
     * metade.
     */
    const moverMarcados = async () => {
        const destino = destinoDaMudanca === '' ? null : destinoDaMudanca;

        setSalvando(true);
        const falhas = [];

        for (const id of marcados) {
            try {
                await updateDocument(id, { kb_category_id: destino });
            } catch (e) {
                falhas.push(id);
            }
        }
        setSalvando(false);

        const movidos = marcados.length - falhas.length;

        if (movidos > 0) {
            toast.success(`${movidos} artigo(s) movido(s).`, { position: toast.POSITION.BOTTOM_CENTER });
        }
        if (falhas.length > 0) {
            toast.error(`${falhas.length} artigo(s) não puderam ser movidos.`, { position: toast.POSITION.BOTTOM_CENTER });
        }

        setDialogMover(false);
        setDestinoDaMudanca('');
        // Só os que falharam seguem marcados: a pessoa pode tentar de novo sem
        // remarcar tudo, e sem mover duas vezes o que já foi.
        setMarcados(falhas);
        carregarArtigos();
        carregarSecoes();
    };

    const alternarMarcado = (id) => setMarcados((atuais) => (
        atuais.includes(id) ? atuais.filter((x) => x !== id) : [...atuais, id]
    ));

    /**
     * Troca UM campo do formulário, com o valor já capturado.
     *
     * ## Por que existe, em vez de cada campo chamar `setFormArtigo` direto
     *
     * O updater funcional do React não roda na hora do `setState`: ele roda no
     * RENDER seguinte. Quem escrevia
     *
     *     onChange={({ target }) => setFormArtigo((f) => ({ ...f, title: target.value }))}
     *
     * estava lendo `target.value` lá dentro, ou seja, NO RENDER -- e nessa hora
     * o React já reescreveu no DOM o valor controlado antigo. O resultado é a
     * digitação ser descartada em silêncio: o `onChange` recebe "Corrigido" e o
     * updater grava "Original".
     *
     * Isso não aparecia ao escrever artigo novo (o campo vem vazio e nada
     * reescreve nada), e aparecia ao EDITAR, onde o campo já tem valor. Foi
     * assim que o título editado não salvava.
     *
     * É a mesma armadilha do botão "Remover" da observação do cliente (20/10):
     * ler de fonte mutável dentro de closure que roda depois. A forma segura é
     * capturar o valor ANTES -- e tê-la num lugar só evita que o próximo campo
     * do formulário repita o erro.
     */
    const trocarNoForm = (campo, valor) => setFormArtigo((f) => ({ ...f, [campo]: valor }));

    const salvarSecao = async () => {
        const nome = nomeDaSecao.trim();
        if (nome === '') return;

        setSalvando(true);
        try {
            if (dialogSecao?.modo === 'renomear') {
                await renomearSecao(dialogSecao.secao.id, { name: nome });
            } else {
                await criarSecao({
                    name: nome,
                    parent_id: dialogSecao?.pai ? dialogSecao.pai.id : null,
                });
            }
            setDialogSecao(null);
            setNomeDaSecao('');
            carregarSecoes();
        } catch (e) {
            toast.error(
                e?.response?.data?.error || 'Não foi possível salvar a seção.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    const removerSecao = async (secao) => {
        try {
            await apagarSecao(secao.id);
            if (selecionada === secao.id) escolherSecao('todas');
            carregarSecoes();
        } catch (e) {
            // A api recusa seção com conteúdo e DIZ o que há dentro. Repetir
            // uma mensagem genérica aqui jogaria fora a única informação que
            // resolve o caso.
            toast.error(
                e?.response?.data?.error || 'Não foi possível apagar a seção.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        }
    };

    const corSecundaria = theme?.palette?.text?.secondary;

    /*
     * A barra do editor: formatação de texto, listas e LINK.
     *
     * O link entra porque um artigo de ajuda aponta para outro e para o vídeo
     * do treinamento -- é o que a central do Movidesk faz e o que o Orby lê
     * para oferecer o material. Imagem não entra: o `storeBase64Image` da api
     * não redimensiona (a biblioteca não está instalada) e colar print de tela
     * aqui encheria a base de imagem em tamanho original.
     */
    const barraDoEditor = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['link'],
                ['clean'],
            ],
        },
        clipboard: { matchVisual: false },
    }), []);

    // As seções em lista plana, para o seletor do formulário de artigo: um
    // <select> não desenha árvore, então o nível vira recuo no rótulo.
    const secoesPlanas = [];
    const achatar = (lista, nivel) => (lista || []).forEach((s) => {
        secoesPlanas.push({ id: s.id, label: `${'— '.repeat(nivel)}${s.name}` });
        achatar(s.children, nivel + 1);
    });
    achatar(secoes, 0);

    return (
        <Grid container spacing={2} data-testid="central-de-ajuda">
            {/* A árvore */}
            <Grid item xs={12} md={3}>
                <Box sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    maxHeight: '70vh',
                    overflowY: 'auto',
                }}>
                    {podeAdministrar && (
                        <Button
                            size="small"
                            startIcon={<Add />}
                            sx={{ mb: 0.5, fontSize: '12px' }}
                            data-testid="nova-secao-raiz"
                            onClick={() => { setDialogSecao({ modo: 'criar', pai: null }); setNomeDaSecao(''); }}
                        >
                            Nova seção
                        </Button>
                    )}
                    <ArvoreDeSecoes
                        secoes={secoes}
                        semCategoria={semCategoria}
                        selecionada={selecionada}
                        aoSelecionar={escolherSecao}
                        podeEditar={podeAdministrar}
                        aoCriarFilha={(pai) => { setDialogSecao({ modo: 'criar', pai }); setNomeDaSecao(''); }}
                        aoRenomear={(secao) => { setDialogSecao({ modo: 'renomear', secao }); setNomeDaSecao(secao.name); }}
                        aoApagar={removerSecao}
                    />
                </Box>
            </Grid>

            {/* A lista, ou o artigo aberto */}
            <Grid item xs={12} md={9}>
                {artigoAberto ? (
                    <Box data-testid="artigo-aberto">
                        <Button
                            size="small"
                            startIcon={<ArrowBack />}
                            onClick={fecharArtigo}
                            data-testid="voltar-da-leitura"
                        >
                            Voltar
                        </Button>

                        {(artigoAberto.trilha || []).length > 0 && (
                            <Breadcrumbs sx={{ mt: 1, fontSize: '12px' }} data-testid="trilha-do-artigo">
                                {artigoAberto.trilha.map((p) => (
                                    <Typography key={p.id} sx={{ fontSize: '12px', color: corSecundaria }}>
                                        {p.name}
                                    </Typography>
                                ))}
                            </Breadcrumbs>
                        )}

                        <Typography variant="h5" sx={{ mt: 1, color: 'text.primary' }}>
                            {artigoAberto.title}
                        </Typography>

                        {/*
                          A assinatura do artigo, como na central deles: tempo de
                          leitura, quem escreveu e quando foi atualizado. Um
                          procedimento fiscal de 2018 sem autor não diz a quem
                          perguntar.
                        */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <AccessTime sx={{ fontSize: 13, color: corSecundaria }} />
                                <Typography sx={{ fontSize: '11.5px', color: corSecundaria }}>
                                    {artigoAberto.minutos_de_leitura} min
                                </Typography>
                            </Box>
                            {artigoAberto.autor && (
                                <Typography sx={{ fontSize: '11.5px', color: corSecundaria }}>
                                    Criado por {artigoAberto.autor}
                                </Typography>
                            )}
                            {artigoAberto.updated_at && (
                                <Typography sx={{ fontSize: '11.5px', color: corSecundaria }}>
                                    Atualizado em {dataLegivel(artigoAberto.updated_at)}
                                    {artigoAberto.atualizado_por ? ` por ${artigoAberto.atualizado_por}` : ''}
                                </Typography>
                            )}
                            {artigoAberto.tem_arquivo && (
                                <Button
                                    size="small"
                                    startIcon={<Download sx={{ fontSize: 14 }} />}
                                    sx={{ fontSize: '11.5px' }}
                                    data-testid="baixar-artigo"
                                    onClick={() => downloadDocument(artigoAberto)}
                                >
                                    Baixar original
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <ConteudoFormatado conteudo={artigoAberto.content} theme={theme} />
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                placeholder="Buscar na central"
                                value={busca}
                                onChange={({ target }) => setBusca(target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { setBuscaAplicada(busca); setPagina(1); }
                                }}
                                sx={{ flexGrow: 1, minWidth: 240 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                                }}
                                inputProps={{ 'data-testid': 'busca-da-central' }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                data-testid="buscar-na-central"
                                onClick={() => { setBuscaAplicada(busca); setPagina(1); }}
                            >
                                Buscar
                            </Button>
                            {/*
                              * "Somente fixados" (Gisele, 23/10/2026). Botão que
                              * fica apertado, e não caixa de seleção: filtro
                              * ligado que não parece ligado é o caminho mais
                              * curto para "sumiram meus manuais".
                              *
                              * Volta para a PÁGINA 1 ao ligar e ao desligar: a
                              * página 4 da lista inteira quase nunca existe na
                              * lista dos fixados, e o resultado seria uma tela
                              * vazia que parece erro.
                              */}
                            <Button
                                variant={somenteFixados ? 'contained' : 'outlined'}
                                size="small"
                                startIcon={somenteFixados ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                                aria-pressed={somenteFixados}
                                data-testid="somente-fixados-na-central"
                                onClick={() => { setSomenteFixados((ligado) => !ligado); setPagina(1); }}
                            >
                                Somente fixados
                            </Button>
                            {/*
                              * "Meus favoritos" — o recorte PESSOAL, separado
                              * do institucional. Volta para a página 1 pelo
                              * mesmo motivo do de cima.
                              */}
                            <Button
                                variant={somenteFavoritos ? 'contained' : 'outlined'}
                                size="small"
                                startIcon={somenteFavoritos ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                                aria-pressed={somenteFavoritos}
                                data-testid="somente-favoritos-na-central"
                                onClick={() => { setSomenteFavoritos((ligado) => !ligado); setPagina(1); }}
                            >
                                Meus favoritos
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Edit />}
                                data-testid="escrever-artigo"
                                onClick={() => {
                                    setFormArtigo({
                                        ...ARTIGO_EM_BRANCO,
                                        // Já vem na seção que está aberta: é quase
                                        // sempre onde o artigo deve entrar.
                                        kb_category_id: (selecionada !== 'todas' && selecionada !== 'sem-categoria')
                                            ? selecionada : '',
                                    });
                                    setDialogArtigo(true);
                                }}
                            >
                                Escrever artigo
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                startIcon={<UploadFile />}
                                disabled={salvando}
                            >
                                Enviar arquivo
                                <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.txt,.doc,.docx"
                                    data-testid="arquivo-da-central"
                                    onChange={({ target }) => {
                                        const arquivo = target.files?.[0];
                                        target.value = '';
                                        enviarArquivo(arquivo);
                                    }}
                                />
                            </Button>
                        </Box>

                        {carregando && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                <CircularProgress size={26} />
                            </Box>
                        )}

                        {!carregando && falhou && (
                            <Typography sx={{ color: corSecundaria, py: 3 }}>
                                Não foi possível carregar os artigos. Tente de novo.
                            </Typography>
                        )}

                        {!carregando && !falhou && artigos.length === 0 && (
                            <Typography sx={{ color: corSecundaria, py: 3 }}>
                                {buscaAplicada.trim() !== ''
                                    ? 'Nenhum artigo encontrado para essa busca.'
                                    : 'Nenhum artigo nesta seção ainda.'}
                            </Typography>
                        )}

                        {!carregando && !falhou && artigos.length > 0 && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: '12px', color: corSecundaria }}>
                                        {total} artigo(s)
                                    </Typography>

                                    {/*
                                      A barra de mover só aparece com algo
                                      marcado: vazia, ela seria um botão que
                                      não faz nada ocupando a lista toda.
                                    */}
                                    {podeAdministrar && marcados.length > 0 && (
                                        <>
                                            <Typography sx={{ fontSize: '12px', color: 'primary.main' }}>
                                                · {marcados.length} marcado(s)
                                            </Typography>
                                            <Button
                                                size="small"
                                                startIcon={<DriveFileMove />}
                                                data-testid="mover-marcados"
                                                disabled={salvando}
                                                onClick={() => { setDestinoDaMudanca(''); setDialogMover(true); }}
                                            >
                                                Mover para seção
                                            </Button>
                                            <Button
                                                size="small"
                                                data-testid="limpar-marcados"
                                                onClick={() => setMarcados([])}
                                            >
                                                Limpar
                                            </Button>
                                        </>
                                    )}
                                </Box>

                                {artigos.map((a) => (
                                    <Box
                                        key={a.id}
                                        data-testid={`artigo-${a.id}`}
                                        onClick={() => abrirArtigo(a.id)}
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            p: 1.25,
                                            mb: 1,
                                            border: 1,
                                            borderColor: a.fixado ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: 'action.hover' },
                                        }}
                                    >
                                        {/*
                                          A marcação fica FORA do clique do
                                          cartão, pelo mesmo motivo do alfinete:
                                          dentro, marcar abriria o artigo.
                                        */}
                                        {podeAdministrar && (
                                            <Checkbox
                                                size="small"
                                                sx={{ alignSelf: 'flex-start', p: 0.25 }}
                                                checked={marcados.includes(a.id)}
                                                inputProps={{
                                                    'aria-label': `Marcar ${a.title}`,
                                                    'data-testid': `marcar-artigo-${a.id}`,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => alternarMarcado(a.id)}
                                            />
                                        )}

                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
                                                {a.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12.5px', color: corSecundaria, mt: 0.25 }}>
                                                {a.resumo}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                                <Typography sx={{ fontSize: '11px', color: corSecundaria }}>
                                                    {a.minutos_de_leitura} min
                                                </Typography>
                                                {a.updated_at && (
                                                    <Typography sx={{ fontSize: '11px', color: corSecundaria }}>
                                                        · {dataLegivel(a.updated_at)}
                                                    </Typography>
                                                )}
                                                {a.visivel_para_cliente && (
                                                    <Chip size="small" label="Visível ao cliente" sx={{ height: 18, fontSize: '10px' }} />
                                                )}
                                            </Box>
                                        </Box>

                                        {/*
                                          O alfinete fica FORA do clique do cartão:
                                          dentro, fixar abriria o artigo no mesmo
                                          clique.
                                        */}
                                        {podeAdministrar && (
                                            <Tooltip title="Editar artigo">
                                                <IconButton
                                                    size="small"
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    aria-label={`Editar ${a.title}`}
                                                    data-testid={`editar-artigo-${a.id}`}
                                                    onClick={(e) => { e.stopPropagation(); abrirEdicao(a); }}
                                                >
                                                    <Edit sx={{ fontSize: 15, color: corSecundaria }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {/*
                                          * A ESTRELA — o favorito DELE
                                          * (Gisele, 23/10/2026). Sem
                                          * permissão e para todo mundo: ela
                                          * muda a ordem na tela de quem
                                          * clicou. O alfinete ao lado é o
                                          * destaque da equipe, e por isso tem
                                          * porta.
                                          */}
                                        <Tooltip title={a.favorito ? 'Tirar dos meus favoritos' : 'Guardar nos meus favoritos'}>
                                            <IconButton
                                                size="small"
                                                sx={{ alignSelf: 'flex-start' }}
                                                aria-label={a.favorito
                                                    ? `Tirar ${a.title} dos meus favoritos`
                                                    : `Guardar ${a.title} nos meus favoritos`}
                                                data-testid={`favoritar-artigo-${a.id}`}
                                                onClick={(e) => { e.stopPropagation(); alternarFavorito(a); }}
                                            >
                                                {a.favorito
                                                    ? <Star sx={{ fontSize: 15, color: 'warning.main' }} />
                                                    : <StarBorder sx={{ fontSize: 15, color: corSecundaria }} />}
                                            </IconButton>
                                        </Tooltip>

                                        {podeFixar && (
                                        <Tooltip title={a.fixado ? 'Desfixar' : 'Fixar no topo'}>
                                            <IconButton
                                                size="small"
                                                sx={{ alignSelf: 'flex-start' }}
                                                aria-label={a.fixado ? `Desfixar ${a.title}` : `Fixar ${a.title} no topo`}
                                                data-testid={`fixar-artigo-${a.id}`}
                                                onClick={(e) => { e.stopPropagation(); alternarFixado(a); }}
                                            >
                                                {a.fixado
                                                    ? <PushPin sx={{ fontSize: 15, color: 'primary.main' }} />
                                                    : <PushPinOutlined sx={{ fontSize: 15, color: corSecundaria }} />}
                                            </IconButton>
                                        </Tooltip>
                                        )}

                                        {/*
                                          * COPIAR O LINK do artigo (Gisele,
                                          * 23/10/2026): "criar um link
                                          * individual para cada
                                          * documento/manual, permitindo o
                                          * acesso direto ao conteúdo".
                                          *
                                          * Para TODOS, e não só para quem
                                          * administra: compartilhar um
                                          * procedimento com um colega é o uso
                                          * comum, e é o que o pedido descreve.
                                          */}
                                        <Tooltip title="Copiar link do artigo (para a equipe)">
                                            <IconButton
                                                size="small"
                                                sx={{ alignSelf: 'flex-start' }}
                                                aria-label={`Copiar link de ${a.title}`}
                                                data-testid={`copiar-link-artigo-${a.id}`}
                                                onClick={(e) => { e.stopPropagation(); copiarLinkDoArtigo(a); }}
                                            >
                                                <LinkOutlined sx={{ fontSize: 15, color: corSecundaria }} />
                                            </IconButton>
                                        </Tooltip>

                                        {/*
                                          * O LINK PARA O CLIENTE (Gisele, 23/10/2026):
                                          * "alguns documentos/manuais poderão ter o
                                          * link compartilhado diretamente com o
                                          * cliente".
                                          *
                                          * São dois links diferentes de propósito: o
                                          * de cima abre a central (precisa de login,
                                          * é para a equipe); este abre uma página
                                          * pública com o conteúdo do artigo.
                                          *
                                          * Só aparece quando o servidor MANDA o
                                          * endereço, e ele só manda para artigo
                                          * marcado como visível ao cliente. Montar o
                                          * endereço aqui daria um botão que copia um
                                          * link respondendo 404 -- e o operador só
                                          * descobriria pelo cliente dizendo que não
                                          * abre.
                                          */}
                                        {a.link_publico && (
                                            <Tooltip title="Copiar link para enviar ao cliente">
                                                <IconButton
                                                    size="small"
                                                    sx={{ alignSelf: 'flex-start' }}
                                                    aria-label={`Copiar link de ${a.title} para o cliente`}
                                                    data-testid={`copiar-link-cliente-${a.id}`}
                                                    onClick={(e) => { e.stopPropagation(); copiarParaOCliente(a); }}
                                                >
                                                    <PublicOutlined sx={{ fontSize: 15, color: 'primary.main' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                ))}

                                {totalDePaginas > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Pagination
                                            size="small"
                                            count={totalDePaginas}
                                            page={pagina}
                                            onChange={(_, p) => setPagina(p)}
                                            data-testid="paginacao-da-central"
                                        />
                                    </Box>
                                )}
                            </>
                        )}

                        {carregandoArtigo && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}
                    </Box>
                )}
            </Grid>

            {/* Escrever / editar artigo -- o MESMO diálogo */}
            <Dialog open={dialogArtigo} onClose={() => setDialogArtigo(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontSize: '1rem' }}>
                    {formArtigo.id !== null ? 'Editar artigo' : 'Escrever artigo'}
                </DialogTitle>
                <DialogContent>
                    {/*
                      O aviso da importação. O editor rico REESCREVE o html que
                      carrega, e os 153 artigos vindos do Movidesk têm tabela e
                      marcação que ele simplifica. Quem edita precisa saber
                      disso ANTES de salvar, não descobrir depois de publicar --
                      e o original continua lá para comparar.
                    */}
                    {formArtigo.origem_url && (
                        <Alert severity="warning" sx={{ mt: 1, fontSize: '12.5px' }} data-testid="aviso-de-importacao">
                            Este artigo foi importado da central do Movidesk. Salvar aqui pode simplificar
                            a formatação original (tabelas e marcação mais elaborada).{' '}
                            <a href={formArtigo.origem_url} target="_blank" rel="noopener noreferrer">
                                Ver o original
                            </a>
                        </Alert>
                    )}

                    <TextField
                        autoFocus fullWidth margin="normal" label="Título" required
                        value={formArtigo.title}
                        onChange={({ target }) => trocarNoForm('title', target.value)}
                        inputProps={{ 'data-testid': 'titulo-do-artigo' }}
                    />
                    <FormControl fullWidth margin="normal" size="small">
                        <InputLabel>Seção</InputLabel>
                        <Select
                            label="Seção"
                            value={formArtigo.kb_category_id}
                            onChange={({ target }) => trocarNoForm('kb_category_id', target.value)}
                            inputProps={{ 'aria-label': 'Seção do artigo' }}
                            SelectDisplayProps={{ 'data-testid': 'secao-do-artigo' }}
                        >
                            <MenuItem value="">Sem seção</MenuItem>
                            {secoesPlanas.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Typography sx={{ fontSize: '12px', color: corSecundaria, mt: 2, mb: 0.5 }}>
                        Conteúdo
                    </Typography>
                    <Box data-testid="editor-do-artigo">
                        <ReactQuill
                            theme="snow"
                            value={formArtigo.content}
                            onChange={(html) => trocarNoForm('content', html)}
                            modules={barraDoEditor}
                            readOnly={salvando}
                            placeholder="Escreva o artigo"
                        />
                    </Box>
                    <Typography sx={{ fontSize: '11.5px', color: corSecundaria, mt: 0.5 }}>
                        É por este texto que o ChatOrby encontra o artigo.
                    </Typography>

                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={(
                            <Checkbox
                                size="small"
                                checked={formArtigo.visivel_para_cliente}
                                inputProps={{ 'data-testid': 'visivel-para-cliente' }}
                                onChange={({ target }) => trocarNoForm('visivel_para_cliente', target.checked)}
                            />
                        )}
                        label={(
                            <Typography sx={{ fontSize: '13px' }}>
                                Visível para o cliente
                                {/*
                                  * O aviso de que "a página ainda não existe"
                                  * SAIU em 26/10: ela existe. Marcar aqui passa
                                  * a liberar, de verdade, o endereço público do
                                  * artigo -- e deixar o texto antigo diria ao
                                  * operador que nada acontece ao marcar.
                                  */}
                                <Typography component="span" sx={{ fontSize: '11.5px', color: corSecundaria, ml: 0.5 }}>
                                    (libera o link público para enviar ao cliente)
                                </Typography>
                            </Typography>
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogArtigo(false)} disabled={salvando}>Cancelar</Button>
                    <Button variant="contained" onClick={salvarArtigo} disabled={salvando} data-testid="salvar-artigo">
                        {salvando
                            ? 'Salvando...'
                            : (formArtigo.id !== null ? 'Salvar' : 'Publicar')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mover os marcados para uma seção */}
            <Dialog open={dialogMover} onClose={() => setDialogMover(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontSize: '1rem' }}>
                    Mover {marcados.length} artigo(s)
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal" size="small">
                        <InputLabel>Seção de destino</InputLabel>
                        <Select
                            label="Seção de destino"
                            value={destinoDaMudanca}
                            onChange={({ target }) => setDestinoDaMudanca(target.value)}
                            inputProps={{ 'aria-label': 'Seção de destino' }}
                            SelectDisplayProps={{ 'data-testid': 'secao-de-destino' }}
                        >
                            <MenuItem value="">Sem seção</MenuItem>
                            {secoesPlanas.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogMover(false)} disabled={salvando}>Cancelar</Button>
                    <Button variant="contained" onClick={moverMarcados} disabled={salvando} data-testid="confirmar-mover">
                        {salvando ? 'Movendo...' : 'Mover'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Criar / renomear seção */}
            <Dialog open={Boolean(dialogSecao)} onClose={() => setDialogSecao(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontSize: '1rem' }}>
                    {dialogSecao?.modo === 'renomear'
                        ? 'Renomear seção'
                        : (dialogSecao?.pai ? `Nova subseção em "${dialogSecao.pai.name}"` : 'Nova seção')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth margin="normal" label="Nome"
                        value={nomeDaSecao}
                        onChange={({ target }) => setNomeDaSecao(target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') salvarSecao(); }}
                        inputProps={{ 'data-testid': 'nome-da-secao' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogSecao(null)} disabled={salvando}>Cancelar</Button>
                    <Button variant="contained" onClick={salvarSecao} disabled={salvando} data-testid="salvar-secao">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default CentralDeAjuda;
